import React, { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Chart from "react-apexcharts";
import profileIcon from "../../resources/icons/id-card.svg";
import userIcon from "../../resources/icons/user-icon.svg";
import crossIcon from "../../resources/icons/cross-icon.svg";
import dangerIcon from "../../resources/icons/danger-icon.svg";
import podcastIcon from "../../resources/icons/podcast-icon.svg";
import { GET } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import RecentlyIssuedCertificates from "../../components/DashboardPageComponents/RecentlyIssuedCertificates";
import ErrorHandling from "../../components/DashboardPageComponents/ErrorHandling";
import CredentialsDetails from "../../components/DashboardPageComponents/CredentialsDetails";
import {
  getColumnChartSeries,
  getColumnChartOptions,
  BAR_CHART_HEIGHT,
} from "../../components/DashboardPageComponents/BarChart";
import {
  getPieChartOptions,
  getPieChartSeries,
  PIE_CHART_HEIGHT,
  PIE_CHART_LEGEND_ITEMS,
} from "../../components/DashboardPageComponents/PieChart";
import AuthContext from "../../store/auth-context";
import { useTranslation } from "react-i18next";
import { automationTestIds } from "../../testing/automationTestIds";
import { pageRootTestIds } from "../../testing/pageRootTestIds";

const Dashboard = () => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["dashboard"]);

  const [totalCredentials, setTotalCredentials] = useState(null);
  const [totalHolders, setTotalHolders] = useState(null);
  const [totalExpired, setTotalExpired] = useState(null);
  const [totalRevoked, setTotalRevoked] = useState(null);
  const [credentialIssuedMonth, setCredentialIssuedMonth] = useState({});
  const [credentialPerMonth, setCredentialPerMonth] = useState({});
  const [credentialDistribution, setCredentialDistribution] = useState(null);
  const [isCredentialsError, setIsCredentialsError] = useState(false);
  const [isBarChartError, setIsBarChartError] = useState(false);
  const [isPieChartError, setIsPieChartError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const userRole =
      authContext.userType?.toLowerCase() ||
      localStorage.getItem("userType")?.toLowerCase();

    if (
      userRole === "programme_approver" ||
      userRole === "approver" ||
      userRole === "tenant_admin" ||
      userRole === "tenant_user"
    ) {
      navigate("/issuer/discover/approvals", { replace: true });
    }
  }, [authContext.userType, navigate]);

  useEffect(() => {
    GET(`${EntityConstants.GET_CREDENTIALS_COUNTS}`)
      .then((response) => response.json())
      .then((data) => {
        setTotalCredentials(data.total_credentials_issued);
        setTotalHolders(data.total_holders);
        setTotalExpired(data.total_expired_credentials);
        setTotalRevoked(data.total_revoked_credentials);
      })
      .catch((error) => {
        console.error("Error fetching the data:", error);
        setIsCredentialsError(true); // Set error flag if the request fails
      });
  }, []);

  useEffect(() => {
    GET(`${EntityConstants.GET_CREDENTIALS_BAR_CHART}`)
      .then((response) => response.json())
      .then((data) => {
        setCredentialIssuedMonth(data.months);
        setCredentialPerMonth(data.counts);
      })
      .catch((error) => {
        console.error("Error fetching bar chart data:", error);
        setIsBarChartError(true);
      });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    GET(`${EntityConstants.GET_CREDENTIALS_PIE_CHART_STATUS}`)
      .then((response) => response.json())
      .then((data) => {
        setCredentialDistribution(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching pie chart data:", error);
        setIsPieChartError(true);
        setIsLoading(false);
      });
  }, []);

  const handleTemplatesClick = () => {
    navigate("/designs");
  };

  const months = credentialIssuedMonth;
  const issuedCredentials = credentialPerMonth;

  const columnChartSeries = getColumnChartSeries(issuedCredentials);
  const columnChartOptions = getColumnChartOptions(months, t);

  const pieChartSeries = getPieChartSeries(credentialDistribution);
  const pieChartOptions = useMemo(() => {
    return getPieChartOptions(t);
  }, [t]);

  return (
    <div
      id={pageRootTestIds.dashboard}
      className="w-full max-w-full overflow-x-hidden px-4 sm:px-6 py-4 sm:py-6 mx-auto min-h-screen mt-[5rem]"
    >
      {isCredentialsError && (
        <div className="mb-6 w-full">
          <ErrorHandling errorMessage="credentials" />
        </div>
      )}
      {/* First Row */}
      <div className="flex flex-col 2xl:flex-row w-full max-w-full gap-0 xl:gap-6 2xl:gap-0">
        <div className="flex flex-col lg:flex-row lg:flex-wrap xl:flex-nowrap w-full 2xl:w-[50%]">
          <div className="w-full max-w-full mb-6 lg:w-1/2 xl:mb-0 xl:w-full">
            <CredentialsDetails
              dataTestId={automationTestIds.dashboardTotalHolders}
              credentialDescription={t("dashboard:total_holders")}
              credentialNumber={totalHolders}
              imageAlt="user-icon"
              imageSrc={userIcon}
              backgroundColor="#E9F8FF"
            />
          </div>

          <div className="w-full max-w-full sm:pl-0 lg:pl-3 sm:pr-0 2xl:pr-3 mb-6 lg:w-1/2 xl:mb-0 xl:w-full 2xl:w-full">
            <CredentialsDetails
              dataTestId={automationTestIds.dashboardTotalCredentials}
              credentialDescription={t("dashboard:total_credentials")}
              credentialNumber={totalCredentials}
              imageAlt="profile-icon"
              imageSrc={profileIcon}
              backgroundColor="#f4f0fc"
            />
          </div>
        </div>

        {/* Second Row */}
        <div className="flex flex-col lg:flex-row lg:flex-wrap xl:flex-nowrap w-full 2xl:w-[50%]">
          <div className="w-full max-w-full mb-6 lg:w-1/2 xl:mb-0 xl:w-full 2xl:w-full">
            <CredentialsDetails
              dataTestId={automationTestIds.dashboardTotalExpired}
              credentialDescription={t("dashboard:total_expired")}
              credentialNumber={totalExpired}
              imageAlt="cross-icon"
              imageSrc={crossIcon}
              backgroundColor="#F6F6F6"
            />
          </div>

          <div className="w-full max-w-full sm:px-0 lg:pl-3 mb-6 lg:w-1/2 xl:mb-0 xl:w-full 2xl:w-full">
            <CredentialsDetails
              dataTestId={automationTestIds.dashboardTotalRevoked}
              credentialDescription={t("dashboard:total_revoked")}
              credentialNumber={totalRevoked}
              imageAlt="danger-icon"
              imageSrc={dangerIcon}
              backgroundColor="#fceeef"
            />
          </div>
        </div>
      </div>
      {totalCredentials === "0" ? (
        <div
          id={automationTestIds.dashboardEmptyState}
          className="flex flex-col flex-wrap sm:mt-0 xl:mt-6 w-full bg-white min-h-screen rounded-2xl"
        >
          <div className="flex flex-col px-2 lg:px-0 mt-20 md:mt-36 justify-center items-center gap-5">
            <img
              alt="certificate-icon"
              src={podcastIcon}
              height="80px"
              width="80px"
              className=""
            />
            <h1 className="text-cfHeadingText font-semibold text-center">
              No credentials issued
            </h1>
            <h3 className="text-xs font-bold text-center lg:text-md text-gray-500">
              Select one of the templates and issue a credential
            </h3>
            <button
              type="button"
              id={automationTestIds.dashboardSeeTemplates}
              className="rounded-lg lg:rounded-xl items-center w-[8rem] h-[2rem] lg:w-[10rem] lg:h-[3rem] lg:px-0 bg-cfPrimaryDarkBlue text-white font-medium lg:font-semibold text-sm lg:text-md"
              onClick={handleTemplatesClick}
            >
              See Templates
            </button>
          </div>
        </div>
      ) : (
        <div>
          {(totalCredentials !== "0" ||
            isBarChartError ||
            isPieChartError ||
            isCredentialsError) && (
            <div>
              <div className="flex flex-col xl:flex-row sm:mt-0 gap-3 xl:mt-6 w-full max-w-full overflow-hidden rounded-2xl">
                <div className="w-full xl:w-[51%] 2xl:w-[49.5%] min-w-0 bg-white rounded-3xl pt-3 flex flex-col">
                  <div className="text-cfHeadingText font-semibold text-lg px-3 shrink-0">
                    {t("dashboard:issuance_trend")}
                  </div>
                  {isBarChartError ? (
                    <ErrorHandling errorMessage={"credentials"} />
                  ) : (
                    <div className="flex flex-1 items-center justify-center w-full min-h-[280px] overflow-hidden px-2 pb-4">
                      {columnChartSeries &&
                        columnChartOptions &&
                        columnChartSeries.length > 0 && (
                          <div
                            className="w-full"
                            id={automationTestIds.dashboardChartBar}
                          >
                            <Chart
                              options={columnChartOptions}
                              series={columnChartSeries}
                              type="bar"
                              width="100%"
                              height={BAR_CHART_HEIGHT}
                            />
                          </div>
                        )}
                    </div>
                  )}
                </div>
                <div className="bg-white w-full xl:w-[49%] 2xl:w-[50.5%] min-w-0 rounded-3xl flex flex-col">
                  <div className="flex justify-start xl:justify-end pt-3 px-3 text-cfHeadingText font-semibold text-lg shrink-0">
                    {t("dashboard:credential_status")}
                  </div>

                  {isLoading ? (
                    <div className="flex flex-1 items-center justify-center w-full min-h-[280px] px-4 py-6">
                      <span className="text-sm text-gray-500">
                        {t("dashboard:loading_chart")}
                      </span>
                    </div>
                  ) : isPieChartError ? (
                    <div className="flex flex-1 items-center justify-center min-h-[280px]">
                      <ErrorHandling errorMessage={"credential statuses"} />
                    </div>
                  ) : (
                    <div className="flex flex-1 flex-col lg:flex-row items-center justify-center gap-6 lg:gap-8 px-4 py-4 w-full min-h-[280px]">
                      {pieChartSeries &&
                        pieChartOptions &&
                        pieChartSeries.length > 0 && (
                          <div
                            className="flex w-full max-w-[320px] flex-1 items-center justify-center min-w-0"
                            id={automationTestIds.dashboardChartPie}
                          >
                            <Chart
                              key={i18n.language}
                              options={pieChartOptions}
                              series={pieChartSeries}
                              type="pie"
                              width="100%"
                              height={PIE_CHART_HEIGHT}
                            />
                          </div>
                        )}
                      <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-1 sm:gap-y-3 shrink-0">
                        {PIE_CHART_LEGEND_ITEMS.map(
                          ({ colorClass, labelKey }) => (
                            <div
                              key={labelKey}
                              className="flex items-center gap-2.5"
                            >
                              <span
                                className={`h-3.5 w-3.5 shrink-0 rounded-full ring-2 ring-white shadow-sm ${colorClass}`}
                              />
                              <span className="text-sm font-medium text-cfHeadingText whitespace-nowrap">
                                {t(labelKey)}
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-6 xl:mt-6 w-full">
                <RecentlyIssuedCertificates />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
